const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Verifica se o erro é de token expirado e redireciona para login
 */
const handleTokenExpired = (response, data) => {
  if (response.status === 401) {
    const mensagem = data?.mensagem || '';
    const isTokenExpired = 
      mensagem.includes('Token inválido') ||
      mensagem.includes('Token expirado') ||
      mensagem.includes('token inválido') ||
      mensagem.includes('token expirado');

    if (isTokenExpired) {
      // Log para debug
      if (process.env.NODE_ENV === 'development') {
        console.warn('[handleTokenExpired] Token expirado detectado. Redirecionando para login...', {
          status: response.status,
          mensagem: mensagem
        });
      }
      // Limpar tokens e redirecionar para login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('primeiro_acesso');
      localStorage.removeItem('user_roles');
      window.location.href = '/login';
      return true;
    } else if (process.env.NODE_ENV === 'development') {
      // Log para debug quando recebe 401 mas não é token expirado
      console.warn('[handleTokenExpired] Recebido 401 mas não é token expirado:', {
        status: response.status,
        mensagem: mensagem,
        data: data
      });
    }
  }
  return false;
};

export const adminService = {
  // ========== USUÁRIOS ==========
  
  // Listar usuários
  async listarUsuarios(pagina = 1, porPagina = 50, ativo = null) {
    let url = `${API_URL}/api/users?pagina=${pagina}&por_pagina=${porPagina}`;
    if (ativo !== null) {
      url += `&ativo=${ativo}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    
    const data = await response.json();
    
    // Verificar se é erro de token expirado
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao listar usuários');
    }
    
    return data.dados;
  },

  // Buscar usuário por ID
  async buscarUsuario(id) {
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    
    const data = await response.json();
    
    // Verificar se é erro de token expirado
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao buscar usuário');
    }
    
    return data.dados;
  },

  // Criar usuário
  async criarUsuario(dados) {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(dados)
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao criar usuário');
    }
    
    return data.dados;
  },

  // Editar usuário
  async editarUsuario(id, dados) {
    // Log para debug
    if (process.env.NODE_ENV === 'development') {
      console.log('[editarUsuario] Enviando requisição:', {
        usuarioId: id,
        dados: { ...dados, senha: dados.senha ? '***' : undefined }
      });
    }
    
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(dados)
    });
    
    const data = await response.json();
    
    // Log da resposta para debug
    if (process.env.NODE_ENV === 'development') {
      console.log('[editarUsuario] Resposta recebida:', {
        status: response.status,
        ok: response.ok,
        mensagem: data.mensagem
      });
    }
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      // Tratamento específico de erros
      if (response.status === 403) {
        // Erro 403 - Sem permissão
        const mensagemErro = data.mensagem || 'Sem permissão para atualizar este usuário';
        
        // Log detalhado para debug
        if (process.env.NODE_ENV === 'development') {
          console.error('[editarUsuario] Erro 403 - Detalhes:', {
            usuarioId: id,
            status: response.status,
            mensagem: mensagemErro,
            dadosEnviados: dados,
            respostaCompleta: data,
            possivelCausa: 'O backend pode ter regras específicas que impedem a atualização deste usuário'
          });
        }
        
        // Criar erro mais informativo
        const erro = new Error(mensagemErro);
        erro.status = 403;
        erro.detalhes = data;
        throw erro;
      } else if (response.status === 404) {
        throw new Error(data.mensagem || 'Usuário não encontrado');
      } else if (response.status === 400) {
        throw new Error(data.mensagem || 'Dados inválidos');
      } else if (response.status === 409) {
        throw new Error(data.mensagem || 'Conflito ao atualizar usuário');
      }
      throw new Error(data.mensagem || `Erro ao editar usuário (${response.status})`);
    }
    
    return data.dados;
  },

  // Bloquear usuário
  async bloquearUsuario(id) {
    const response = await fetch(`${API_URL}/api/users/${id}/bloquear`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    
    const data = await response.json();
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao bloquear usuário');
    }
    
    return data;
  },

  // Desbloquear usuário
  async desbloquearUsuario(id) {
    const response = await fetch(`${API_URL}/api/users/${id}/desbloquear`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    
    const data = await response.json();
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao desbloquear usuário');
    }
    
    return data;
  },

  // Excluir usuário
  async excluirUsuario(id) {
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    const data = await response.json();
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao excluir usuário');
    }
    
    return data;
  },

  // ========== ROLES ==========
  
  // Listar roles
  async listarRoles(pagina = 1, porPagina = 50) {
    const response = await fetch(`${API_URL}/api/roles?pagina=${pagina}&por_pagina=${porPagina}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    
    const data = await response.json();
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao listar roles');
    }
    
    return data.dados;
  },

  // Buscar role por ID
  async buscarRole(id) {
    const response = await fetch(`${API_URL}/api/roles/${id}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    
    const data = await response.json();
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao buscar role');
    }
    
    return data.dados;
  },

  // Criar role
  async criarRole(dados) {
    const response = await fetch(`${API_URL}/api/roles`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(dados)
    });
    
    const data = await response.json();
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao criar role');
    }
    
    return data.dados;
  },

  // Editar role
  async editarRole(id, dados) {
    const response = await fetch(`${API_URL}/api/roles/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(dados)
    });
    
    const data = await response.json();
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao editar role');
    }
    
    return data.dados;
  },

  // Excluir role
  async excluirRole(id) {
    const response = await fetch(`${API_URL}/api/roles/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    const data = await response.json();
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao excluir role');
    }
    
    return data;
  },

  // ========== ATRIBUIÇÃO DE ROLES ==========
  
  // Listar roles de um usuário (RECOMENDADO - usa ID numérico)
  async listarRolesUsuario(usuarioId) {
    const response = await fetch(`${API_URL}/api/users/${usuarioId}/roles`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    
    const data = await response.json();
    
    // Verificar se é erro de token expirado
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      // Log detalhado para debug
      console.error('Erro ao listar roles do usuário:', {
        status: response.status,
        statusText: response.statusText,
        data: data,
        url: `${API_URL}/api/users/${usuarioId}/roles`
      });
      
      // Mensagem de erro mais específica
      if (response.status === 404) {
        throw new Error('Usuário não encontrado');
      } else if (response.status === 403) {
        throw new Error('Sem permissão para acessar este recurso');
      } else {
        throw new Error(data.mensagem || `Erro ao listar roles do usuário (${response.status})`);
      }
    }
    
    // Verificar se a estrutura de resposta está correta
    if (!data.dados) {
      console.warn('Resposta inesperada do servidor:', data);
      // Retornar estrutura padrão se não houver dados
      return { roles: [] };
    }
    
    return data.dados;
  },

  // Atualizar roles de um usuário (RECOMENDADO - usa ID numérico)
  async atualizarRolesUsuario(usuarioId, rolesIdentificadores) {
    // Validar entrada
    if (!usuarioId) {
      throw new Error('ID do usuário é obrigatório');
    }
    
    if (!Array.isArray(rolesIdentificadores)) {
      throw new Error('rolesIdentificadores deve ser um array');
    }
    
    // Log para debug
    if (process.env.NODE_ENV === 'development') {
      console.log('[atualizarRolesUsuario] Enviando requisição:', {
        usuarioId,
        rolesCount: rolesIdentificadores.length,
        roles: rolesIdentificadores
      });
    }
    
    const payload = { roles: rolesIdentificadores };
    
    const response = await fetch(`${API_URL}/api/users/${usuarioId}/roles`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    
    const data = await response.json();
    
    // Log da resposta para debug
    if (process.env.NODE_ENV === 'development') {
      console.log('[atualizarRolesUsuario] Resposta recebida:', {
        status: response.status,
        ok: response.ok,
        data: data
      });
    }
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      // Tratamento específico de erros
      if (response.status === 400) {
        throw new Error(data.mensagem || 'Dados inválidos. Verifique se os UUIDs das roles estão corretos.');
      } else if (response.status === 403) {
        throw new Error(data.mensagem || 'Sem permissão para atualizar roles deste usuário');
      } else if (response.status === 404) {
        throw new Error(data.mensagem || 'Usuário não encontrado');
      } else if (response.status === 409) {
        throw new Error(data.mensagem || 'Conflito ao atualizar roles');
      }
      throw new Error(data.mensagem || `Erro ao atualizar roles (${response.status})`);
    }
    
    return data.dados;
  },

  // ========== ATRIBUIÇÃO DE ROLES (Alternativas - usando UUID) ==========
  
  // Listar roles de um usuário (alternativa com UUID)
  async listarRolesUsuarioPorUUID(usuarioIdentificador) {
    const response = await fetch(`${API_URL}/api/roles/usuarios/${usuarioIdentificador}/roles`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    
    const data = await response.json();
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao listar roles do usuário');
    }
    
    return data.dados;
  },

  // Atribuir role a usuário (alternativa com UUID - uma role por vez)
  // @param {string} usuarioIdentificador - UUID do usuário (não o ID numérico)
  // @param {string} roleId - UUID da role (não o ID numérico)
  // @returns {Promise<Object>} Dados do vínculo criado
  // Endpoint: POST /api/roles/usuarios/{usuario_uuid}/roles
  // Payload: { "role_id": "uuid-da-role" }
  async atribuirRoleUsuario(usuarioIdentificador, roleId) {
    const response = await fetch(`${API_URL}/api/roles/usuarios/${usuarioIdentificador}/roles`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ role_id: roleId })
    });
    
    const data = await response.json();
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      // Tratamento específico de erros
      if (response.status === 400) {
        throw new Error(data.mensagem || 'role_id inválido ou faltando');
      } else if (response.status === 404) {
        throw new Error(data.mensagem || 'Usuário ou role não encontrado');
      } else if (response.status === 409) {
        throw new Error(data.mensagem || 'Role já atribuída ao usuário');
      }
      throw new Error(data.mensagem || 'Erro ao atribuir role');
    }
    
    return data.dados;
  },

  // Remover role de usuário (alternativa com UUID)
  async removerRoleUsuario(usuarioIdentificador, roleIdentificador) {
    const response = await fetch(`${API_URL}/api/roles/usuarios/${usuarioIdentificador}/roles/${roleIdentificador}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    const data = await response.json();
    
    if (handleTokenExpired(response, data)) {
      throw new Error('Token expirado. Redirecionando para login...');
    }
    
    if (!response.ok) {
      throw new Error(data.mensagem || 'Erro ao remover role');
    }
    
    return data;
  }
};

